import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ThumbsUp, ThumbsDown, RefreshCw, TrendingUp, Users, MessageSquare, BarChart2 } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ExpertRatings = () => {
  const [ratings, setRatings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [skip]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [ratingsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_BASE}/superadmin/ratings?limit=${limit}&skip=${skip}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/superadmin/ratings/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRatings(skip === 0 ? ratingsRes.data.ratings : [...ratings, ...ratingsRes.data.ratings]);
      setTotalCount(ratingsRes.data.total_count);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      toast.error('Failed to load expert ratings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setSkip(0);
    fetchData();
  };

  const loadMore = () => {
    setSkip(prev => prev + limit);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const RatingCard = ({ rating }) => {
    const [expanded, setExpanded] = useState(false);
    const isThumbsUp = rating.rating === 'thumbs_up';
    const truncatedFeedback = rating.feedback?.length > 150
      ? rating.feedback.substring(0, 150) + '...'
      : rating.feedback;

    return (
      <div
        className={`p-4 rounded-lg border-2 transition-all ${
          isThumbsUp
            ? 'bg-green-50 border-green-200 hover:bg-green-100'
            : 'bg-red-50 border-red-200 hover:bg-red-100'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {isThumbsUp ? (
              <ThumbsUp className="w-6 h-6 text-green-600" />
            ) : (
              <ThumbsDown className="w-6 h-6 text-red-600" />
            )}
            <div>
              <p className="font-semibold text-slate-900">{rating.expert_email}</p>
              <p className="text-sm text-slate-600">{formatTimestamp(rating.created_at)}</p>
            </div>
          </div>
          <Badge className={isThumbsUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
            {isThumbsUp ? 'Positive' : 'Needs Review'}
          </Badge>
        </div>

        <div className="mb-2">
          <p className="text-slate-700">
            {expanded ? rating.feedback : truncatedFeedback}
          </p>
          {rating.feedback?.length > 150 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium mt-1"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Response ID:</span>
          <code className="px-2 py-1 bg-slate-100 rounded font-mono">
            {rating.response_id?.substring(0, 12)}...
          </code>
        </div>
      </div>
    );
  };

  if (loading && skip === 0) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading expert ratings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-emerald-600" />
              Expert Ratings Dashboard
            </h1>
            <p className="text-slate-600 mt-2">
              Monitor AI response quality across the platform
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Ratings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{analytics.total_ratings}</p>
                  <p className="text-xs text-slate-500 mt-1">All time</p>
                </div>
                <BarChart2 className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Thumbs Up</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-green-600">{analytics.thumbs_up_count}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {analytics.thumbs_up_percentage?.toFixed(1)}% positive
                  </p>
                </div>
                <ThumbsUp className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Thumbs Down</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-red-600">{analytics.thumbs_down_count}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(100 - analytics.thumbs_up_percentage).toFixed(1)}% need review
                  </p>
                </div>
                <ThumbsDown className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Active Experts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{analytics.active_experts}</p>
                  <p className="text-xs text-slate-500 mt-1">Contributing</p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Activity Card */}
      {analytics?.recent_7_days && (
        <Card className="mb-8 border-2 border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Last 7 Days Activity</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">{analytics.recent_7_days.total_ratings}</p>
              </div>
              <div>
                <p className="text-slate-600">Thumbs Up</p>
                <p className="text-2xl font-bold text-green-600">{analytics.recent_7_days.thumbs_up_count}</p>
              </div>
              <div>
                <p className="text-slate-600">Thumbs Down</p>
                <p className="text-2xl font-bold text-red-600">{analytics.recent_7_days.thumbs_down_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Experts */}
      {analytics?.top_experts && analytics.top_experts.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Top Contributing Experts</CardTitle>
            <CardDescription>Most active expert reviewers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_experts.slice(0, 5).map((expert, index) => (
                <div key={expert.expert_email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{expert.expert_email}</p>
                      <p className="text-sm text-slate-600">{expert.rating_count} ratings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-green-600">
                      <ThumbsUp className="w-4 h-4" />
                      <span className="font-semibold">{expert.thumbs_up_count}</span>
                    </div>
                    <div className="flex items-center gap-1 text-red-600">
                      <ThumbsDown className="w-4 h-4" />
                      <span className="font-semibold">{expert.thumbs_down_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ratings List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Ratings</CardTitle>
          <CardDescription>
            {ratings.length} of {totalCount} ratings shown
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ratings.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">No expert ratings yet</p>
              <p className="text-sm text-slate-400">
                Ratings will appear here as experts review AI responses
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {ratings.map((rating) => (
                <RatingCard key={rating.id} rating={rating} />
              ))}

              {ratings.length < totalCount && (
                <div className="text-center pt-4">
                  <Button
                    onClick={loadMore}
                    disabled={loading}
                    variant="outline"
                    className="w-full"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500"></div>
                        Loading...
                      </div>
                    ) : (
                      `Load More (${ratings.length} / ${totalCount})`
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpertRatings;
